import { Alignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ClickEvent, ClickListener, ContextMenuItemCreator, ContextMenuLabelProps, ImageProps, MultiButton,
    ScreenStack
} from "@mcleod/components";
import { AppTableHeadingPanel } from "./AppTableHeadingPanel";
import { FabStyles } from "./FabStyles";

const defaultBorderRadius = 4;

export class FloatingActionButton extends MultiButton {
    private readonly tableHeadingPanel: AppTableHeadingPanel
    private readonly _fabImageProps: Partial<ImageProps> = {
        color: "primary.reverse",
        width: 24,
        height: 24
    };
    private _primaryClickListener: ClickListener;
    private _secondaryClickListener: ClickListener;
    private _fabScrollListener: EventListener =
        (event: Event) => this.handleFabOnScroll(event);

    private _initialFabCaption: string;

    constructor(actionsPanel: AppTableHeadingPanel) {
        super();
        this.tableHeadingPanel = actionsPanel;
        this.add(this.secondaryButton);
        this.parent = this.tableHeadingPanel;
        this._element.classList.add(FabStyles.fabBase);
        this.addMountListener(() => {
            this.tableHeadingPanel.doOnFabMount();
            this.addScrollEventListener();
        });
        this.addUnmountListener(() => {
            this.tableHeadingPanel.doOnFabUnmount();
        });
    }

    protected override get defaultProps() {
        return {
            ...super.defaultProps,
            id: "fab",
            maxHeight: 60,
            minWidth: 60,
            maxWidth: 220,
            visible: false,
            borderRadius: defaultBorderRadius,
            borderShadow: true,
            backgroundColor: "primary",
            zIndex: ScreenStack.getNewHighestZIndex()
        };
    }

    protected override createPrimaryButton(): Button {
        const button = super.createPrimaryButton();
        button.setProps({
            minWidth: 60,
            width: "auto",
            maxWidth: 220,
            fillRow: true,
            height: "auto",
            fillHeight: true,
            padding: 0,
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 0,
            paddingBottom: 0,
            fontSize: "large",
            color: "primary.reverse",
            wrap: false,
            imageProps: {
                ...this._fabImageProps,
                margin: 8
            }
        });
        return button;
    }

    protected override createSecondaryButton(): Button {
        const button = super.createSecondaryButton();
        button.setProps({
            minWidth: 60,
            width: this.secondaryButtonWidth,
            maxWidth: null,
            fillRow: false,
            height: "auto",
            fillHeight: true,
            color: "primary.reverse",
            fontSize: "large",
            padding: 0,
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 0,
            paddingBottom: 0,
            imageWidth: 24,
            imageHeight: 24,
            imageProps: {
                ...this._fabImageProps,
                name: ImageName.ellipsis
            },
        });
        return button;
    }

    syncActions() {
        this.syncPrimaryButton();
        this.syncSecondaryButton();
        this.visible = this.primaryButton.visible || this.secondaryButton.visible;
        this.showSeparator = this.primaryButton.visible && this.secondaryButton.visible;
        this.recordFabCaption();
        this.tableHeadingPanel.handleTableBodyPadding();
    }

    private syncPrimaryButton() {
        if (this._primaryClickListener) {
            this.primaryButton.removeClickListener(this._primaryClickListener);
            this._primaryClickListener = null;
        }

        const action = this.tableHeadingPanel.primaryAction
        if (action != null) {
            this._primaryClickListener = (event: ClickEvent) => action.clicked(event);
            this.primaryButton.setProps({
                caption: action.caption || action.displayLabel || null,
                visible: true,
                imageProps: {
                    ...this._fabImageProps,
                    name: action.imageName
                },
                onClick: this._primaryClickListener
            });
        } else {
            this.primaryButton.visible = false;
            this.primaryButton.removeClickListener(this._primaryClickListener);
        }
    }

    private syncSecondaryButton() {
        if (this._secondaryClickListener) {
            this.secondaryButton.removeClickListener(this._secondaryClickListener);
            this._secondaryClickListener = null;
        }

        const actions = this.tableHeadingPanel.secondaryActions;
        if (actions.length == 0)
            this.secondaryButton.visible = false;
        else if (actions.length == 1)
            this.setupSingleActionSecondary(actions[0]);
        else
            this.setupMultiActionSecondary(actions);
    }

    private setupSingleActionSecondary(action: Button) {
        if (action != null) {
            this._secondaryClickListener = (event: ClickEvent) => action.clicked(event);
            this.secondaryButton.setProps({
                caption: this.primaryButton.visible? null : (action.caption || action.displayLabel || null),
                visible: true,
                fillRow: !(this.primaryButton && this.primaryButton.visible),
                borderRadius: this.primaryButton.visible ? 0 : defaultBorderRadius,
                borderTopRightRadius: defaultBorderRadius,
                borderBottomRightRadius: defaultBorderRadius,
                imageProps: {
                    ...this._fabImageProps,
                    name: action.imageName
                },
                onClick: this._secondaryClickListener
            });
        } else {
            this.secondaryButton.visible = false;
        }
    }

    private setupMultiActionSecondary(actions: Button[]) {
        this.secondaryButton.setProps({
            caption: this.primaryButton.visible ? null : "Table Tools",
            width: this.secondaryButtonWidth,
            imageAlign: Alignment.RIGHT,
            visible: actions != null && actions.length > 0,
            fillRow: !(this.primaryButton && this.primaryButton.visible),
            borderRadius: this.primaryButton.visible ? 0 : defaultBorderRadius,
            borderTopRightRadius: defaultBorderRadius,
            borderBottomRightRadius: defaultBorderRadius,
            imageProps: {
                ...this._fabImageProps,
                name: ImageName.ellipsis,
                marginLeft: this.primaryButton.visible ? this.secondaryButton.imageProps.marginLeft : 8
            },
        });

        this.secondaryButton.contextMenuItems = this.getContextMenuItems(actions);
    }

    private get secondaryButtonWidth(): string {
        return this.primaryButton.visible ? "25%" : "100%";
    }

    public getContextMenuItems(actions: Button[]): Partial<ContextMenuLabelProps>[] {
        const array: Partial<ContextMenuLabelProps>[] = [];
        actions.forEach((b: Button) => array.push(
            ContextMenuItemCreator.fromButton(b)
        ));
        return array;
    }

    protected override setSecondaryButtonAction(button: Button) {
        // If using a ContextMenu (multi-action secondary button) instead of the list label implementation
        // provided in MultiButton, setting the contextMenuItems on secondaryButton component creates
        // the click listener. Doing it here, as well, would make the menu open twice.

        // Otherwise, we are managing the click listener of the secondary button in this class, since
        // it might change as syncing occurs.
    }

    private addScrollEventListener() {
        this.getParentLayout()._element.addEventListener(
            'scroll',
            this._fabScrollListener,
            { capture: true }
        );
    }

    private handleFabOnScroll(event: Event) {
        const scrollTarget = event.target as HTMLElement;
        const scrollTop = scrollTarget.scrollTop;

        // Prevents collapse of fab when scrolling on x-axis, if user could.
        if (scrollTarget.scrollWidth > scrollTarget.clientWidth)
            return;

        const mainActionButton = this.primaryButton.visible ?
            this.primaryButton : (this.secondaryButton.visible ? this.secondaryButton : null);
        if (mainActionButton)
            mainActionButton.caption = scrollTop <= 0 ? this._initialFabCaption : null;
    }

    private recordFabCaption(): void {
        if (this.primaryButton.visible)
            this._initialFabCaption = this.primaryButton.caption;
        else if (this.secondaryButton.visible)
            this._initialFabCaption = this.secondaryButton.caption;
    }
}
